import React, { forwardRef, useCallback, useEffect } from 'react'
import cn from 'classnames'
import { usePrevious } from 'react-use'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@material-ui/core'
import { selectionColumnId } from '../table-hooks/useSelectColumn'
import { useLegacyTableStyles } from 'core/table/table-hooks/useLegacyTableStyles'
import { useTableStyles } from 'core/table/table-hooks/useTableStyles'
import { isFunction } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useInternationalizationEnabled } from 'i18n/feature'
import { renderTableColumnHeader } from '../utils'

const PAGE_SIZES = [10, 25, 50, 100]

// Memoized per row: `page` can hold hundreds of rows when expanded groups are not paginated, and
// every mutable flag react-table writes onto the row has to be passed in so the comparison sees it.
const PagedTableRow = React.memo(function PagedTableRow({ row, classes, cellClassName, getCellStyle }) {
  return (
    <TableRow {...row.getRowProps()}>
      {row.cells.map(cell => (
        <TableCell
          {...cell.getCellProps([
            {
              ...(isFunction(getCellStyle) && getCellStyle(row, cell)),
            },
          ])}
          className={cn(classes.cell, cellClassName, cell.column.className, {
            [classes.highlight]: row.original?.isNewInCollection,
          })}
        >
          <div>
            {cell.isGrouped
              ? cell.render('Cell')
              : cell.isAggregated
              ? cell.render('Aggregated')
              : cell.isPlaceholder
              ? null
              : /* For cells with repeated values, render null, otherwise, just render the regular cell */
                cell.render('Cell')}
          </div>
        </TableCell>
      ))}
    </TableRow>
  )
})

const PagedTable = forwardRef((props, ref) => {
  const {
    state: { pageIndex, pageSize },
    getTableProps,
    headerGroups,
    page,
    rows,
    prepareRow,
    gotoPage,
    setPageSize,
    style,
    groupBy,
    setGroupBy,
    selectHeaderCellClassName,
    wrapHeader,
    newStyles,
    header,
    getCellStyle,
    getHeaderCellStyle,
    compactView,
    totalCount,
    onPageChange,
    onPageSizeChange,
    controlledPageIndex,
  } = props

  const { t } = useTranslation('admin')
  const internationalizationEnabled = useInternationalizationEnabled()
  const prevData = usePrevious(props.data)
  const paginationCount = totalCount != null ? totalCount : rows.length
  const paginationPageIndex = controlledPageIndex ?? pageIndex

  /* automatically toggle on grouping */
  useEffect(() => {
    if (groupBy && props.data.length && props.data !== prevData) {
      setGroupBy(groupBy)
    }
  }, [groupBy, prevData, props.data, setGroupBy])

  const handleChangePage = (event, newPage) => {
    if (onPageChange) {
      onPageChange(newPage)
      return
    }
    gotoPage(newPage)
  }

  const handleChangePageSize = event => {
    const nextPageSize = parseInt(event.target.value, 10)
    if (onPageSizeChange) {
      onPageSizeChange(nextPageSize)
      return
    }
    setPageSize(nextPageSize)
    gotoPage(0)
  }

  const oldClasses = useLegacyTableStyles()
  const newClasses = useTableStyles()
  const classes = newStyles ? newClasses : oldClasses

  const getSortStyle = useCallback(
    column => {
      if (column.canSort && column.isSorted) {
        return column.isSortedDesc ? classes.sortDesc : classes.sortAsc
      }

      return null
    },
    [classes],
  )

  // Column visibility and ordering change the cells without changing row identity, so the row
  // memoization has to key off them explicitly.
  const columnsKey = (props.visibleColumns || []).map(column => column.id).join(',')

  return (
    <TableContainer component={Paper} className={cn({ [classes.compactView]: compactView })}>
      {header}
      <Table ref={ref} {...getTableProps()} classes={{ root: classes.table }}>
        <TableHead>
          {headerGroups.map(headerGroup => (
            <TableRow style={style} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <TableCell
                  {...column.getHeaderProps([
                    {
                      ...(isFunction(getHeaderCellStyle) && getHeaderCellStyle(column)),
                    },
                    column.getSortByToggleProps(),
                  ])}
                  className={cn(getSortStyle(column), classes.cell, classes.headerCell, column.className, {
                    [classes.filterHeader]: column.filterValue && !column.disableFilters,
                    [selectHeaderCellClassName]: column.id === selectionColumnId,
                    [classes.wrapHeader]: wrapHeader,
                  })}
                >
                  <div>
                    {column.canSort ? (
                      <TableSortLabel active={column.isSorted} direction={column.isSortedDesc ? 'desc' : 'asc'}>
                        {renderTableColumnHeader(column)}
                      </TableSortLabel>
                    ) : (
                      renderTableColumnHeader(column)
                    )}
                  </div>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>

        <TableBody>
          {page.map(row => {
            prepareRow(row)

            return (
              <PagedTableRow
                key={row.id}
                row={row}
                classes={classes}
                cellClassName={props.classes.cell}
                getCellStyle={getCellStyle}
                isSelected={row.isSelected}
                isSomeSelected={row.isSomeSelected}
                isExpanded={row.isExpanded}
                isNewInCollection={row.original?.isNewInCollection}
                columnsKey={columnsKey}
              />
            )
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              classes={{
                root: classes.pagination,
                toolbar: classes.pagination,
                spacer: classes.spacer,
                menuItem: classes.pagination,
                caption: classes.pagination,
                input: classes.pagination,
                selectRoot: classes.pagination,
                select: classes.pagination,
                actions: classes.pagination,
              }}
              count={paginationCount}
              page={paginationPageIndex}
              rowsPerPage={pageSize}
              rowsPerPageOptions={PAGE_SIZES}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangePageSize}
              {...(internationalizationEnabled
                ? {
                    labelRowsPerPage: t('table.pagination.rowsPerPage'),
                    labelDisplayedRows: ({ from, to, count }) =>
                      t('table.pagination.displayedRows', { from, to, count }),
                  }
                : {})}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  )
})

export default PagedTable

PagedTable.defaultProps = {
  classes: {
    cell: '',
    headerCell: '',
  },
}
